import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';
import backend from 'utils/backend';

import {Box, ButtonBase, Stack, TextField, Typography} from 'views/components';
import {Translate, translate} from 'views/module';
import {AssigneeBadge} from '../../../components';

const PlaybookStepPrompt = (props) => {
  const {stepPrompt, setStepPrompt, addStep, defaultDay, setDefaultDay, skills} = props;

  const [assignees, setAssignees] = React.useState([]);
  const [selectedSkill, setSelectedSkill] = React.useState(null);
  const [day, setDay] = React.useState(null);
  const [admins, setAdmins] = React.useState([]);
  const [adminsLoading, setAdminsLoading] = React.useState(false);
  const [listVisible, setListVisible] = React.useState(false);
  const [commandType, setCommandType] = React.useState(null);
  const [commandQuery, setCommandQuery] = React.useState('');
  const [highlightedOptionIndex, setHighlightedOptionIndex] = React.useState(0);
  const [trackedCommandStart, setTrackedCommandStart] = React.useState(null);

  const skipCommandInputRef = React.useRef(false);
  const promptFieldRef = React.useRef(null);
  const promptHighlightRef = React.useRef(null);

  const detectDayFromPrompt = React.useCallback((promptValue) => {
    const dayMatch = /\bday\s+(\d+)\b/i.exec(promptValue);

    if (!dayMatch) {
      return null;
    }

    return Number(dayMatch[1]);
  }, []);

  const escapeRegExp = React.useCallback((value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), []);

  const closeCommandMenu = React.useCallback(() => {
    setListVisible(false);
    setCommandType(null);
    setCommandQuery('');
    setTrackedCommandStart(null);
    setHighlightedOptionIndex(0);
  }, []);

  const sortedSkills = React.useMemo(() => {
    return [...(Array.isArray(skills) ? skills : [])]
        .sort((left, right) => (left?.name || '').localeCompare(right?.name || ''));
  }, [skills]);

  const filteredAdmins = admins.filter((admin) => {
    const query = commandQuery.toLowerCase();
    const adminName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim().toLowerCase();
    const adminRn = (admin?.userRn || '').toLowerCase();

    return adminName.includes(query) || adminRn.includes(query);
  });

  const filteredSkills = React.useMemo(() => {
    const query = commandQuery.toLowerCase();

    return sortedSkills.filter((skill) => {
      const skillName = (skill?.name || '').toLowerCase();
      const skillRn = (skill?.skillRn || '').toLowerCase();

      return skillName.includes(query) || skillRn.includes(query);
    });
  }, [commandQuery, sortedSkills]);

  const insertAssignee = React.useCallback((admin) => {
    if (trackedCommandStart === null) {
      return;
    }

    const displayName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim() || admin?.userRn || '';
    const currentMentionMatch = /@[^@\s]*$/.exec(stepPrompt.slice(trackedCommandStart));
    const mentionLength = currentMentionMatch?.[0]?.length || 0;
    const nextValue = `${stepPrompt.slice(0, trackedCommandStart)}@${displayName}${stepPrompt.slice(trackedCommandStart + mentionLength)}`;

    skipCommandInputRef.current = true;
    setStepPrompt(nextValue);
    setAssignees((currentAssignees) => {
      const nextAssignees = currentAssignees.filter((assignee) => assignee?.userRn !== admin?.userRn);
      return [...nextAssignees, admin];
    });
    closeCommandMenu();
  }, [closeCommandMenu, setStepPrompt, stepPrompt, trackedCommandStart]);

  const insertSkill = React.useCallback((skill) => {
    if (trackedCommandStart === null) {
      return;
    }

    const skillName = skill?.name || '';
    const currentSkillMatch = /\/[^/\s]*$/.exec(stepPrompt.slice(trackedCommandStart));
    const skillLength = currentSkillMatch?.[0]?.length || 0;
    const nextValue = `${stepPrompt.slice(0, trackedCommandStart)}/${skillName}${stepPrompt.slice(trackedCommandStart + skillLength)}`;

    skipCommandInputRef.current = true;
    setStepPrompt(nextValue);
    setSelectedSkill(skill);
    closeCommandMenu();
  }, [closeCommandMenu, setStepPrompt, stepPrompt, trackedCommandStart]);

  const submitStep = React.useCallback(() => {
    const trimmedPrompt = stepPrompt.trim();

    if (!trimmedPrompt) {
      return;
    }

    const payload = {
      prompt: trimmedPrompt,
      day: day ?? defaultDay,
      ownerRns: assignees.map((assignee) => assignee?.userRn).filter(Boolean),
    };
    if (selectedSkill?.skillRn) {
      payload.skillRn = selectedSkill.skillRn;
    };

    addStep(payload);
    setDefaultDay(Math.max(defaultDay, day ?? defaultDay));
    setStepPrompt('');
    setAssignees([]);
    setSelectedSkill(null);
    setDay(null);
    closeCommandMenu();
  }, [addStep, assignees, closeCommandMenu, day, defaultDay, selectedSkill, stepPrompt]);

  const handleCommandKeyDown = React.useCallback((event) => {
    if (listVisible) {
      if (event.key === 'Escape') {
        skipCommandInputRef.current = true;
        event.preventDefault();
        closeCommandMenu();
        return;
      }

      const filteredOptions = commandType === 'mention' ? filteredAdmins : filteredSkills;
      const isLoadingOptions = commandType === 'mention' && adminsLoading;

      if (isLoadingOptions || filteredOptions.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        skipCommandInputRef.current = true;
        event.preventDefault();
        setHighlightedOptionIndex((prev) => (prev + 1) % filteredOptions.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        skipCommandInputRef.current = true;
        event.preventDefault();
        setHighlightedOptionIndex((prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length);
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        const selectedOption = filteredOptions[highlightedOptionIndex] || filteredOptions[0];

        if (selectedOption) {
          skipCommandInputRef.current = true;
          event.preventDefault();
          if (commandType === 'mention') {
            insertAssignee(selectedOption);
          } else {
            insertSkill(selectedOption);
          }
        }
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitStep();
    }
  }, [adminsLoading, commandType, filteredAdmins, filteredSkills, highlightedOptionIndex, insertAssignee, insertSkill, listVisible, submitStep]);

  React.useEffect(() => {
    setHighlightedOptionIndex(0);
  }, [commandQuery]);

  React.useEffect(() => {
    setDay(detectDayFromPrompt(stepPrompt));
  }, [detectDayFromPrompt, stepPrompt]);

  React.useEffect(() => {
    setAssignees((currentAssignees) => currentAssignees.filter((assignee) => {
      const assigneeDisplayName = `${assignee?.firstName || ''} ${assignee?.lastName || ''}`.trim() || assignee?.userRn || '';

      if (!assigneeDisplayName) {
        return false;
      }

      return stepPrompt.includes(`@${assigneeDisplayName}`);
    }));
  }, [stepPrompt]);

  React.useEffect(() => {
    const matchedSkills = sortedSkills.reduce((matches, skill) => {
      const skillName = skill?.name || '';

      if (!skillName) {
        return matches;
      }

      const skillMatcher = new RegExp(`(^|\\s)\\/${escapeRegExp(skillName)}(?=\\s|$)`, 'g');
      let skillMatch = skillMatcher.exec(stepPrompt);

      while (skillMatch) {
        matches.push({
          skill,
          index: skillMatch.index + skillMatch[1].length,
        });
        skillMatch = skillMatcher.exec(stepPrompt);
      }

      return matches;
    }, []);

    if (matchedSkills.length === 0) {
      setSelectedSkill(null);
      return;
    }

    matchedSkills.sort((left, right) => left.index - right.index);
    setSelectedSkill(matchedSkills[matchedSkills.length - 1].skill);
  }, [escapeRegExp, sortedSkills, stepPrompt]);

  React.useEffect(() => {
    if (skipCommandInputRef.current) {
      skipCommandInputRef.current = false;
      return;
    }

    const mentionMatch = /(^|\s)@([^\s@]*)$/.exec(stepPrompt);

    if (mentionMatch) {
      const commandStart = mentionMatch.index + mentionMatch[1].length;

      setTrackedCommandStart(commandStart);
      setCommandType('mention');
      setListVisible(true);
      setCommandQuery((mentionMatch[2] || '').trimStart().toLowerCase());
      return;
    }

    const skillMatch = /(^|\s)\/([^/\s]*)$/.exec(stepPrompt);

    if (!skillMatch) {
      closeCommandMenu();
      return;
    }

    const commandStart = skillMatch.index + skillMatch[1].length;

    setTrackedCommandStart(commandStart);
    setCommandType('skill');
    setListVisible(true);
    setCommandQuery((skillMatch[2] || '').trimStart().toLowerCase());
  }, [closeCommandMenu, stepPrompt]);

  React.useEffect(() => {
    if (!listVisible || commandType !== 'mention') {
      return;
    }

    let mounted = true;
    const timeoutId = setTimeout(async () => {
      setAdminsLoading(true);
      try {
        const filter = [
          {key: 'status', value: ['ACTIVE']},
          {key: 'role', value: 'ADMIN'},
        ];
        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.user.queryUser(filter, sortBy);
        const uniqueOptions = Array.from(
            new Map((result?.list ?? []).map((admin) => [admin.userRn, admin])).values(),
        );

        if (mounted) {
          setAdmins(uniqueOptions);
        }
      } catch (error) {
        console.error('Failed to load admins', error);
      } finally {
        if (mounted) {
          setAdminsLoading(false);
        }
      }
    }, 150);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [commandType, listVisible]);

  React.useEffect(() => {
    const inputElement = promptFieldRef.current?.querySelector('input');
    const highlightElement = promptHighlightRef.current;

    if (!inputElement || !highlightElement) {
      return undefined;
    }

    const syncHighlightScroll = () => {
      highlightElement.style.transform = `translateX(-${inputElement.scrollLeft}px)`;
    };

    syncHighlightScroll();
    inputElement.addEventListener('scroll', syncHighlightScroll);

    return () => {
      inputElement.removeEventListener('scroll', syncHighlightScroll);
    };
  }, [stepPrompt]);

  const renderHighlightedPrompt = () => {
    if (!stepPrompt) {
      return null;
    }

    const highlightRanges = [];
    if (assignees.length > 0) {
      assignees.forEach((assignee) => {
        const assigneeDisplayName = `${assignee?.firstName || ''} ${assignee?.lastName || ''}`.trim() || assignee?.userRn || '';

        if (!assigneeDisplayName) {
          return;
        }

        const mentionMatcher = new RegExp(`@${escapeRegExp(assigneeDisplayName)}`, 'g');
        let mentionMatch = mentionMatcher.exec(stepPrompt);

        while (mentionMatch) {
          highlightRanges.push({
            start: mentionMatch.index,
            end: mentionMatch.index + mentionMatch[0].length,
            type: 'mention',
          });
          mentionMatch = mentionMatcher.exec(stepPrompt);
        }
      });
    } else {
      const currentMentionMatch = /(^|\s)(@[^@\s]*)$/.exec(stepPrompt);

      if (currentMentionMatch) {
        const mentionValue = currentMentionMatch[2];
        const mentionStart = currentMentionMatch.index + currentMentionMatch[1].length;

        highlightRanges.push({
          start: mentionStart,
          end: mentionStart + mentionValue.length,
          type: 'mention',
        });
      }
    }

    if (selectedSkill?.name) {
      const skillMatcher = new RegExp(`/${escapeRegExp(selectedSkill.name)}`, 'g');
      let skillMatch = skillMatcher.exec(stepPrompt);

      while (skillMatch) {
        highlightRanges.push({
          start: skillMatch.index,
          end: skillMatch.index + skillMatch[0].length,
          type: 'skill',
        });
        skillMatch = skillMatcher.exec(stepPrompt);
      }
    }

    const dayMatcher = /\bday\s+\d+\b/gi;
    let dayMatch = dayMatcher.exec(stepPrompt);

    while (dayMatch) {
      highlightRanges.push({
        start: dayMatch.index,
        end: dayMatch.index + dayMatch[0].length,
        type: 'day',
      });
      dayMatch = dayMatcher.exec(stepPrompt);
    }

    highlightRanges.sort((left, right) => left.start - right.start);

    const segments = [];
    let cursor = 0;

    highlightRanges.forEach((range, index) => {
      if (range.start < cursor) {
        return;
      }

      if (range.start > cursor) {
        segments.push(
            <Box
              component='span'
              key={`text-${cursor}`}
              sx={{color: '#1A1916'}}
            >
              {stepPrompt.slice(cursor, range.start)}
            </Box>,
        );
      }

      segments.push(
          <Box
            component='span'
            key={`${range.type}-${range.start}-${index}`}
            sx={{
              color: range.type === 'mention' ? '#958ACC' : range.type === 'skill' ? '#DA7955' : '#1A1916',
              backgroundColor: range.type === 'day' ? '#F5EEDF' : 'transparent',
              borderRadius: range.type === 'day' ? '4px' : 0,
              boxDecorationBreak: range.type === 'day' ? 'clone' : 'slice',
              WebkitBoxDecorationBreak: range.type === 'day' ? 'clone' : 'slice',
              boxShadow: 'none',
            }}
          >
            {stepPrompt.slice(range.start, range.end)}
          </Box>,
      );

      cursor = range.end;
    });

    if (cursor < stepPrompt.length) {
      segments.push(
          <Box
            component='span'
            key={`text-${cursor}-end`}
            sx={{color: '#1A1916'}}
          >
            {stepPrompt.slice(cursor)}
          </Box>,
      );
    }

    return segments;
  };

  return (
    <Stack sx={{gap: '8px'}}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
        }}
      >
        {
          !!stepPrompt &&
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              px: '16px',
              py: '16px',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '150%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <Box
              ref={promptHighlightRef}
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '150%',
                fontFamily: 'inherit',
                letterSpacing: 'inherit',
                whiteSpace: 'nowrap',
                width: 'max-content',
              }}
            >
              {renderHighlightedPrompt()}
            </Box>
          </Box>
        }
        <Box ref={promptFieldRef}>
          <TextField
            value={stepPrompt}
            onChange={setStepPrompt}
            onKeyDown={handleCommandKeyDown}
            onBlur={() => {
              window.setTimeout(() => {
                closeCommandMenu();
              }, 100);
            }}
            placeholder={translate('playbooks.New.StepEditor.Placeholder')}
            sx={{
              'width': '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: 'transparent',
                position: 'relative',
                zIndex: 2,
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '150%',
                fontFamily: 'inherit',
                letterSpacing: 'inherit',
                color: 'transparent',
                caretColor: '#1A1916',
                WebkitTextFillColor: 'transparent',
                whiteSpace: 'nowrap',
                overflowX: 'auto',
                overflowY: 'hidden',
                position: 'relative',
                zIndex: 2,
                px: '16px',
                py: '16px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                backgroundColor: 'transparent',
              },
              '& .MuiOutlinedInput-input::placeholder': {
                color: '#A8A49E',
                opacity: 1,
              },
            }}
          />
        </Box>
        {
          listVisible &&
          <Box
            sx={{
              position: 'absolute',
              top: 'calc(100% + 1px)',
              left: 0,
              right: 0,
              maxHeight: '240px',
              overflowY: 'auto',
              border: '1px solid #E8E4DE',
              borderRadius: '12px',
              backgroundColor: '#FFFFFF',
              zIndex: 2147483647,
            }}
          >
            {
              commandType === 'mention' && adminsLoading &&
              <Typography
                sx={{
                  px: '12px',
                  py: '10px',
                  fontSize: '12px',
                  color: '#6B6760',
                }}
              >
                <Translate expression='playbooks.New.StepEditor.Loading' />
              </Typography>
            }
            {
              ((commandType === 'mention' && !adminsLoading && filteredAdmins.length === 0) ||
                (commandType === 'skill' && filteredSkills.length === 0)) &&
              <Typography
                sx={{
                  px: '12px',
                  py: '10px',
                  fontSize: '12px',
                  color: '#6B6760',
                }}
              >
                <Translate expression='playbooks.New.StepEditor.NoMatches' />
              </Typography>
            }
            {
              commandType === 'mention' &&
              !adminsLoading &&
              filteredAdmins.map((admin, index) => {
                const displayName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim() || admin?.userRn;
                const isHighlighted = index === highlightedOptionIndex;

                return (
                  <ButtonBase
                    key={admin.userRn}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      insertAssignee(admin);
                    }}
                    sx={{
                      'width': '100%',
                      'justifyContent': 'flex-start',
                      'px': '12px',
                      'py': '10px',
                      'backgroundColor': isHighlighted ? '#F3F1EE' : '#FFFFFF',
                      '&:hover': {
                        backgroundColor: '#F8F6F3',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#1A1916',
                        lineHeight: '150%',
                      }}
                    >
                      @{displayName}
                    </Typography>
                  </ButtonBase>
                );
              })
            }
            {
              commandType === 'skill' &&
              filteredSkills.map((skill, index) => {
                const skillName = skill?.name || skill?.skillRn;
                const isHighlighted = index === highlightedOptionIndex;

                return (
                  <ButtonBase
                    key={skill.skillRn}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      insertSkill(skill);
                    }}
                    sx={{
                      'width': '100%',
                      'justifyContent': 'flex-start',
                      'px': '12px',
                      'py': '10px',
                      'backgroundColor': isHighlighted ? '#F3F1EE' : '#FFFFFF',
                      '&:hover': {
                        backgroundColor: '#F8F6F3',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#1A1916',
                        lineHeight: '150%',
                      }}
                    >
                      /{skillName}
                    </Typography>
                  </ButtonBase>
                );
              })
            }
          </Box>
        }
      </Box>
      <Typography
        sx={{
          color: '#A8A49E',
          fontSize: '13px',
          lineHeight: '100%',
        }}
      >
        <Translate expression='playbooks.New.StepEditor.Footer' />
      </Typography>
      {
        assignees.length > 0 &&
        <Stack
          direction='row'
          alignItems='center'
          sx={{
            gap: '8px',
          }}
        >
          <Typography
            sx={{
              color: '#6B6760',
              fontSize: '12px',
              lineHeight: '100%',
            }}
          >
            {translate('playbooks.New.StepEditor.AssignedTo', {name: ''}).trim()}
          </Typography>
          <Stack
            direction='row'
            alignItems='center'
            sx={{
              pl: '6px',
            }}
          >
            {
              assignees.map((assignee, index) => (
                <AssigneeBadge
                  key={assignee.userRn}
                  interactive={false}
                  user={assignee}
                  sx={{
                    width: '24px',
                    height: '24px',
                    position: 'relative',
                    zIndex: index + 1,
                    fontSize: '11px',
                    outline: '2px solid',
                    outlineColor: '#FFFFFF',
                    ml: index === 0 ? 0 : '-6px',
                  }}
                />
              ))
            }
          </Stack>
        </Stack>
      }
      {
        day !== null &&
        <Typography
          sx={{
            color: '#6B6760',
            fontSize: '12px',
            lineHeight: '100%',
          }}
        >
          <Translate
            expression='playbooks.New.StepEditor.Day'
            substitute={{day}}
          />
        </Typography>
      }
    </Stack>
  );
};

PlaybookStepPrompt.propTypes = {
  setStepPrompt: PropTypes.func.isRequired,
  stepPrompt: PropTypes.string.isRequired,
  addStep: PropTypes.func.isRequired,
  defaultDay: PropTypes.number.isRequired,
  setDefaultDay: PropTypes.func.isRequired,
  skills: PropTypes.array.isRequired,

  user: PropTypes.object,
};

const mapStateToProps = (state) => ({
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps, dispatch)(React.memo(PlaybookStepPrompt));
